import type { IMarketGroupChanceHistory } from "~/feature/chart/types";
import type { Platform } from "~/types/platform";
import { formatPercentageValue } from "~/utils/formatValue";

const GRIDLINE_COUNT = 5;
const CONSENSUS_ID = "consensus";

export interface IEmbedSparklineMarket {
  id: string;
  platform: Platform;
}

interface IEmbedSparklineParams {
  history: IMarketGroupChanceHistory[];
  markets: IEmbedSparklineMarket[];
  isConsensusMode: boolean;
  consensusOnly: boolean;
}

interface ISparklinePoint {
  time: number;
  value: number;
}

export interface IEmbedSparklineSeries {
  id: string;
  isConsensus: boolean;
  platform?: Platform;
  path: string;
  end: { x: number; y: number; label: string };
}

export interface IEmbedSparklineGridline {
  y: number;
  label?: string;
}

export interface IEmbedSparkline {
  series: IEmbedSparklineSeries[];
  gridlines: IEmbedSparklineGridline[];
}

export interface ISparklineAxisLabel {
  key: string;
  y: number;
  label: string;
  color?: string;
}

const LABEL_GAP = 14;
// A label is centred on its y, so half a gap keeps it inside the plot.
const MIN_LABEL_Y = LABEL_GAP / 2;
const MAX_LABEL_Y = 100 - LABEL_GAP / 2;

const clampLabelY = (y: number): number =>
  Math.max(MIN_LABEL_Y, Math.min(y, MAX_LABEL_Y));

const spread = (labels: ISparklineAxisLabel[]): ISparklineAxisLabel[] => {
  const placed = [...labels]
    .sort((first, second) => first.y - second.y)
    .map((label) => ({ ...label }));

  placed.forEach((label, index) => {
    const previous = placed[index - 1];
    const floor = previous ? previous.y + LABEL_GAP : MIN_LABEL_Y;

    label.y = Math.max(label.y, floor);
  });

  for (let index = placed.length - 1; index >= 0; index -= 1) {
    const next = placed[index + 1];
    const ceiling = next ? next.y - LABEL_GAP : MAX_LABEL_Y;

    placed[index].y = Math.max(MIN_LABEL_Y, Math.min(placed[index].y, ceiling));
  }

  return placed;
};

export const getSparklineAxisLabels = (
  endValues: ISparklineAxisLabel[],
  gridlines: IEmbedSparklineGridline[]
): ISparklineAxisLabel[] => {
  const values = spread(endValues);
  const fits = (y: number) =>
    values.every((label) => Math.abs(label.y - y) >= LABEL_GAP);

  return [
    ...values,
    ...gridlines.flatMap(({ y, label }) => {
      const labelY = clampLabelY(y);

      return label && fits(labelY) ? [{ key: `grid-${y}`, y: labelY, label }] : [];
    }),
  ];
};

const EMPTY: IEmbedSparkline = { series: [], gridlines: [] };

const collectPoints = (
  history: IMarketGroupChanceHistory[],
  read: (
    bucket: IMarketGroupChanceHistory["historical"][number]
  ) => number | undefined
): ISparklinePoint[] =>
  history
    .flatMap(({ historical }) => historical)
    .flatMap((bucket) => {
      const value = read(bucket);
      const time = new Date(bucket.timestamp).getTime();

      return typeof value === "number" &&
        Number.isFinite(value) &&
        Number.isFinite(time)
        ? [{ time, value }]
        : [];
    })
    .sort((a, b) => a.time - b.time);

const scale = (value: number, min: number, max: number): number =>
  max === min ? 50 : ((max - value) / (max - min)) * 100;

const toPath = (
  points: ISparklinePoint[],
  timeRange: { min: number; max: number },
  valueRange: { min: number; max: number }
): string => {
  // One reading is still a value worth showing: a flat line across the box.
  if (points.length === 1) {
    const y = Number(
      scale(points[0].value, valueRange.min, valueRange.max).toFixed(2)
    );

    return `M0,${y} L100,${y}`;
  }

  return points
    .map(({ time, value }, index) => {
      const x =
        timeRange.max === timeRange.min
          ? index * 100
          : ((time - timeRange.min) / (timeRange.max - timeRange.min)) * 100;

      return `${index ? "L" : "M"}${Number(x.toFixed(2))},${Number(
        scale(value, valueRange.min, valueRange.max).toFixed(2)
      )}`;
    })
    .join(" ");
};

// Top, middle and bottom carry a value; a flat range has only one value to
// show, so only the middle line (where the flat series sits) is labelled.
const getGridlines = (min: number, max: number): IEmbedSparklineGridline[] =>
  Array.from({ length: GRIDLINE_COUNT }, (_, index) => {
    const ratio = index / (GRIDLINE_COUNT - 1);
    const isEdge = index === 0 || index === GRIDLINE_COUNT - 1;
    const isMiddle = index === (GRIDLINE_COUNT - 1) / 2;
    const isLabelled = max === min ? isMiddle : isEdge || isMiddle;

    return {
      y: ratio * 100,
      label: isLabelled
        ? formatPercentageValue(max - (max - min) * ratio)
        : undefined,
    };
  });

export const getEmbedSparkline = ({
  history,
  markets,
  isConsensusMode,
  consensusOnly,
}: IEmbedSparklineParams): IEmbedSparkline => {
  const showConsensus = isConsensusMode;
  const showPlatforms = !(isConsensusMode && consensusOnly);

  const platformSeries = showPlatforms
    ? markets.map(({ id, platform }) => ({
        id,
        isConsensus: false,
        points: collectPoints(
          history,
          (bucket) =>
            bucket.byPlatform.find((item) => item.marketId === id)?.yesChance
        ),
        platform,
      }))
    : [];

  const consensusSeries = showConsensus
    ? [
        {
          id: CONSENSUS_ID,
          isConsensus: true,
          points: collectPoints(
            history,
            (bucket) => bucket.consensus?.yesChance
          ),
          platform: undefined,
        },
      ]
    : [];

  const all = [...platformSeries, ...consensusSeries].filter(
    ({ points }) => points.length > 0
  );

  if (!all.length) return EMPTY;

  const values = all.flatMap(({ points }) => points.map(({ value }) => value));
  const times = all.flatMap(({ points }) => points.map(({ time }) => time));
  const valueRange = { min: Math.min(...values), max: Math.max(...values) };
  const timeRange = { min: Math.min(...times), max: Math.max(...times) };

  return {
    series: all.map(({ id, isConsensus, platform, points }) => {
      const last = points[points.length - 1];

      return {
        id,
        isConsensus,
        platform,
        path: toPath(points, timeRange, valueRange),
        end: {
          x: 100,
          y: Number(
            scale(last.value, valueRange.min, valueRange.max).toFixed(2)
          ),
          label: formatPercentageValue(last.value, {
            needToAbs: true,
            maximumFractionDigits: 1,
          }),
        },
      };
    }),
    gridlines: getGridlines(valueRange.min, valueRange.max),
  };
};
